import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";

import { getFieldConfigByFieldId } from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";

import { type FormSelectChoice, type InputSelectComponentProps } from "../../../lib/types/types";
import { FormFieldInputSelect } from "../FormFieldInputSelect/FormFieldInputSelect";

interface StatusComponentProps<T = FormSelectChoice> extends InputSelectComponentProps<T> {
  segmentId: string;
  errorId?: string;
  explanation?: string | JSX.Element;
}

export const Status = ({
  value,
  isErrored,
  errorId = "invalidStatus",
  options,
  onChange,
  intl,
  placeholder = "",
  onKeyDown = undefined,
  segmentId,
  disabled = false,
  label = <FormattedHTMLMessage id="status" defaultMessage="Status" />,
  explanation = undefined,
}: StatusComponentProps) => {
  const inputId = `${segmentId}-status`;
  const hasOnlyOneOption = options.length === 1;
  const isFieldHidden =
    getFieldConfigByFieldId("status")?.hidden || getFieldConfigByFieldId("statuses")?.hidden;

  // Auto select a status if there is only one option. Available statuses can
  // change after initial render.
  // Uses values instead of arrays for stable identities.
  const onlyOption = hasOnlyOneOption ? options[0] : undefined;
  const selectedValue = value?.value;
  React.useEffect(() => {
    if (onlyOption && selectedValue !== onlyOption.value) {
      onChange(onlyOption);
    }
  }, [onlyOption?.value, selectedValue]);

  if (hasOnlyOneOption || isFieldHidden) {
    return null;
  }

  return (
    <FormFieldInputSelect
      fieldId={inputId}
      options={options}
      onChange={(status: FormSelectChoice) => onChange(status)}
      onKeyDown={onKeyDown}
      value={options.find((option) => option.value === (value && value.value))}
      placeholder={
        placeholder || intl.formatHTMLMessage({ id: "statusPlaceholder", defaultMessage: "Status" })
      }
      showPlaceholderAndHideLabel={!!placeholder}
      isRequired
      containerId={`sid-${inputId}`}
      label={label}
      disabled={disabled}
      explanation={explanation}
      errorMessage={
        isErrored && (
          <FormattedHTMLMessage
            id={`errorId.${errorId}`}
            defaultMessage={`Invalid ${segmentId} status`}
          />
        )
      }
    />
  );
};

export const StatusComponent = injectIntl(Status);
